import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import Button, { Color } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { ActivityIndicator } from '@sorare/core/src/components/user/ActivityIndicator';
import Avatar from '@sorare/core/src/components/user/Avatar';
import FollowButton from '@sorare/core/src/components/user/FollowButton';
import { FOOTBALL_MY_CLUB_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { Link } from '@sorare/core/src/routing/Link';

import { MembersListRowActions } from 'components/rivalsUserGroup/MembersListRowActions';

import { MembersListRow_userGroupMembershipInterface } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  padding: var(--double-unit);
`;
const CenterContent = styled(Horizontal).attrs({ gap: 1.5 })`
  flex: 1;
`;
type Props = {
  userGroupMembership: Nullable<MembersListRow_userGroupMembershipInterface>;
  isCurrentUserAdmin: boolean;
  userGroupId: Nullable<string>;
};

export const MembersListRow = ({
  userGroupMembership,
  isCurrentUserAdmin,
  userGroupId,
}: Props) => {
  const { user, administrator } = userGroupMembership || {};
  return (
    <Wrapper>
      <CenterContent
        as={Link}
        to={generatePath(FOOTBALL_MY_CLUB_SLUG, {
          slug: user?.slug || '',
        })}
      >
        {user && (
          <ActivityIndicator user={user}>
            <Avatar user={user} size={4} rounded />
          </ActivityIndicator>
        )}
        <Vertical gap={0}>
          <BodyM>{user?.nickname}</BodyM>
          {administrator && (
            <LabelM color="var(--c-rivals-purple)">
              <FormattedMessage
                id="MembersListRow.captain"
                defaultMessage="Captain"
              />
            </LabelM>
          )}
        </Vertical>
      </CenterContent>
      {isCurrentUserAdmin && userGroupId ? (
        <MembersListRowActions user={user} userGroupId={userGroupId} />
      ) : (
        user && (
          <FollowButton
            size="small"
            subscribable={user}
            initialSubscription={user.followed}
            renderButton={(buttonProps, subscription, hovered) => {
              let followButtonColor: Color = 'secondary';
              if (subscription && hovered) {
                followButtonColor = 'red';
              } else if (subscription) {
                followButtonColor = 'tertiary';
              }
              return (
                <Button
                  size="small"
                  {...buttonProps}
                  color={followButtonColor}
                />
              );
            }}
          />
        )
      )}
    </Wrapper>
  );
};

MembersListRow.fragments = {
  userGroupMembershipInterface: gql`
    fragment MembersListRow_userGroupMembershipInterface on UserGroupMembershipInterface {
      id
      administrator
      user {
        slug
        followed {
          slug
        }
        nickname
        ...Avatar_user
        ...ActivityIndicator_user
        ...MembersListRowActions_user
      }
    }
    ${ActivityIndicator.fragments.user}
    ${Avatar.fragments.user}
    ${MembersListRowActions.fragments.user}
  ` as TypedDocumentNode<MembersListRow_userGroupMembershipInterface>,
};
